import * as stylex from "@stylexjs/stylex";
import { copy } from "@/copy";
import type { CalendarEvent, Tag } from "@/domain/schemas";
import { whoLabel } from "@/domain/tags";
import { type Clock, clockAt, formatDuration, minutesOfDay, timeParts } from "@/domain/time";
import type { TodayHero } from "@/domain/todayHero";
import "@/theme/tokens.stylex";
import { colors, radii, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { press } from "@/ui/press";
import { Skeleton, SkeletonBlock } from "@/ui/Skeleton";
import { CueEyebrow } from "./CueEyebrow";
type EventHero = Extract<TodayHero<CalendarEvent>, {
  event: unknown;
}>;
type HeroSummaryProps = {
  hero: EventHero;
  tags: readonly Tag[];
  clock: Clock;
  tz: string;
  onOpenEvent: (event: CalendarEvent) => void;
};
const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1v2ro7d",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  button: {
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kmVPX3: "x1717udv",
    k9WMMc: "x1yc453h",
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  skeleton: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xddi9bj",
    $$css: true
  }
};
const timeAt = (iso: string, clock: Clock, tz: string) => timeParts(minutesOfDay(new Date(iso), tz), clock).time;
export function HeroSummary({
  hero,
  tags,
  clock,
  tz,
  onOpenEvent
}: HeroSummaryProps) {
  const countdown = countdownText(hero);
  return <button type="button" onClick={() => onOpenEvent(hero.event)} {...stylex.props(styles.root, styles.button, press.scale)}>
      <span className="x78zum5 x6s0dn4 x1a02dak x1v2ro7d">
        <CueEyebrow>{hero.kind === "now" ? copy.today.hero.now : copy.today.hero.next}</CueEyebrow>
        {countdown && <span className="xfenp3h xnnvkky xqhrb7l x12ulsxz xaope02 xr0iwhn xif65rj xkv8mnm xtvhhri xuxw1ft">{countdown}</span>}
      </span>
      <span className="xdjib4s x1v94iar x1f41ap9 xipi2l9 x1fzhlzt x104kibb x1h7i4cw x1ua5tub xb3r6kr">{hero.event.title}</span>
      <span className="x12feioo xbt1u0e xwn7fz2 x1fzhlzt">{eventMeta(hero, tags, clock, tz)}</span>
    </button>;
}
export function HeroSkeleton() {
  return <Skeleton label={copy.today.hero.loading} xstyle={styles.skeleton}>
      <SkeletonBlock width={140} height={14} />
      <SkeletonBlock width="80%" height={40} radius={radii.md} />
      <SkeletonBlock width="50%" height={18} />
    </Skeleton>;
}
function countdownText(hero: EventHero): string | undefined {
  if (hero.kind === "next") return copy.today.hero.in(formatDuration(hero.in));
  return hero.left === undefined ? undefined : copy.today.hero.left(formatDuration(hero.left));
}
function runningMeta({
  startAt,
  endAt
}: CalendarEvent, clock: Clock, tz: string): string {
  if (endAt === undefined) return copy.today.hero.started(clockAt(startAt, clock, tz));
  return copy.today.hero.until(timeAt(endAt, clock, tz));
}
function eventMeta(hero: EventHero, tags: readonly Tag[], clock: Clock, tz: string) {
  const when = hero.kind === "now" ? runningMeta(hero.event, clock, tz) : clockAt(hero.event.startAt, clock, tz);
  const who = whoLabel(hero.event, tags, copy.events.everyone);
  return [hero.event.location, when, who].filter(Boolean).join(" · ");
}